import React, { useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../contexts/StoreContext';
import GestureArea from '../../gesture-area/GestureArea';
import {
  GestureStateChangeEvent,
  GestureUpdateEvent,
  PanGestureHandlerEventPayload,
  PinchGestureHandlerEventPayload,
} from 'react-native-gesture-handler';
import { UserImageWithTransform as UserImage } from '../../user-image/UserImageWithTransform';
import { IUserImageAreaModel } from '../../../store/_models/userImageArea';
import { IUserImageModel } from '../../../store/_models/userImage';
import { calculateZoomValue } from '../../../helpers/zoom.helper';
import { UserImageAreaEmpty } from '@photosi/rn-components/user-image-area/UserImageAreaEmpty';
import { UserImageArea } from '@photosi/rn-components/user-image-area/UserImageArea';
import { UserImageAreaGridOverlay } from '@photosi/rn-components/user-image-area/user-image-area-grid-overlay/UserImageAreaGridOverlay';
import { useWheelZoom } from '../../../hooks/useWheelZoom';
import { BadgeQualityAlert } from '@photosi/rn-components/badge-quality-alert/BadgeQualityAlert';
import { PreviewSvgModelInstance } from '../../../store/_models/previewSvg';
import { PreviewCanvasModelInstance } from '../../../store/_models/previewCanvas';
import { UserImageAreaFocusOverlay } from '@photosi/rn-components/user-image-area/user-image-area-focus-overlay/UserImageAreaFocusOverlay';
import { LayoutChangeEvent, StyleProp, View } from 'react-native';
import { ViewStyle } from '@expo/html-elements/build/primitives/View';
import { PreviewPrintModelInstance } from '../../../store/_models/previewPrint';
import { emit } from '../../../contexts/TransformEventBus';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { useRoute } from '@react-navigation/native';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { neutrals } from 'src/theme/colors';

export type PreviewContentProps = {
  preview: PreviewSvgModelInstance | PreviewCanvasModelInstance | PreviewPrintModelInstance;
  editable: boolean;
  showGrid: boolean;
  disabled?: boolean;
  disableSwapImageAnimation?: boolean;
  qualityBadgePadding?: number;
  style?: StyleProp<ViewStyle>;
  showBadges?: boolean;
  emptyStateWithText?: boolean;
};

/**
 * Represents the collage of all the images to be inserted into the
 * photo space (userImageAreas) in the product (userImagesForPreviewSvg).
 */
export const PreviewContent = observer(
  ({
    preview,
    editable = false,
    showGrid = false,
    disabled = false,
    disableSwapImageAnimation = false,
    qualityBadgePadding = 0,
    style,
    showBadges = true,
    emptyStateWithText = false,
  }: PreviewContentProps) => {
    const {
      project: {
        selectUserImage,
        activeUserImageArea,
        activeProductId,
        showSliderZoomTutorial,
        setShowSliderZoomTutorial,
      },
      tutorial: { showTutorial, onNextHandler, activeStep, setTutorialArea },
    } = useStore();
    const route = useRoute();
    const newRoute = [ROUTE_NAMES.EditorNew].includes(route?.name as ROUTE_NAMES);
    const editRoute = [ROUTE_NAMES.Edit].includes(route?.name as ROUTE_NAMES);
    const lastPinchZoomRef = useRef<number | undefined>();
    const [hoveringAreaId, setHoveringAreaId] = useState<string | null>(null);

    const { isDesktop } = useResponsiveScreen();

    // Zoom with wheel logic
    const isHoveringOnActiveUserImageArea = !!hoveringAreaId && hoveringAreaId === activeUserImageArea?.id;
    const isZoomWithWheelActive = isHoveringOnActiveUserImageArea && !preview.isAnyUserImageUpdating;
    useWheelZoom({ isActive: isZoomWithWheelActive, userImage: activeUserImageArea?.userImage });

    /**
     * GESTURE HANDLERS
     */

    // Calculate the new image scale using a dynamic speed approach
    const calculateScaleFrom = (userImage: IUserImageModel, eventScale: number) => {
      if (lastPinchZoomRef.current === undefined) lastPinchZoomRef.current = eventScale;
      const zoomFactor = eventScale / lastPinchZoomRef.current;
      lastPinchZoomRef.current = eventScale;
      return calculateZoomValue(userImage.scale * zoomFactor, userImage.minScale, userImage.maxScale);
    };

    const pinchHandler = (event: GestureUpdateEvent<PinchGestureHandlerEventPayload>, userImage: IUserImageModel) => {
      if (!editable) return;
      const newScale = calculateScaleFrom(userImage, event.scale);

      // userImage.setScale(newScale, responsiveScale);
      emit({
        type: 'temp-zoom',
        userImageId: userImage.id,
        payload: { absoluteScale: newScale },
        source: 'gesture',
      });
    };

    const pinchEndHandler = (
      event: GestureStateChangeEvent<PinchGestureHandlerEventPayload>,
      userImage: IUserImageModel,
    ) => {
      if (!editable) return;
      const newScale = calculateScaleFrom(userImage, event.scale);

      // userImage.applyScale(newScale, responsiveScale);
      emit({
        type: 'apply-zoom',
        userImageId: userImage.id,
        payload: { absoluteScale: newScale },
        source: 'gesture',
      });

      lastPinchZoomRef.current = undefined;
    };

    /**
     * SELECT IMAGE HANDLERS
     */
    const tapHandler = ({ userImage }: IUserImageAreaModel) => {
      selectUserImage(userImage);
    };

    /**
     * DRAG IMAGE HANDLERS
     */
    const dragImageChange = (event: GestureUpdateEvent<PanGestureHandlerEventPayload>, userImage: IUserImageModel) => {
      if (!editable) return;
      // userImage.setOffsets({ x: event.translationX, y: event.translationY });

      // NUOVO: Emit evento temporaneo direttamente da gesture (UI thread)
      emit({
        type: 'temp-pan',
        userImageId: userImage.id,
        payload: { x: event.translationX, y: event.translationY },
        source: 'gesture',
      });
    };

    // const showZoomTutorial = () => {
    //   setTimeout(() => {
    //     showTutorial({
    //       tutorialId: 'SliderZoom',
    //       stepId: 'step-1',
    //       isDesktop: isDesktop,
    //     });

    //     setTimeout(() => {
    //       onNextHandler(activeStep.onPressNext);
    //     }, 5000);
    //   }, 1000);
    // };

    const dragImageEnd = (
      event: GestureStateChangeEvent<PanGestureHandlerEventPayload>,
      userImage: IUserImageModel,
    ) => {
      if (!editable) return;
      // userImage.applyOffsets({ x: event.translationX, y: event.translationY }, responsiveScaleSV.value);

      // NUOVO: Emit evento apply direttamente da gesture (UI thread)
      emit({
        type: 'apply-pan',
        userImageId: userImage.id,
        payload: { x: event.translationX, y: event.translationY },
        source: 'gesture',
      });
      // in modal edit, shows tutorial on how to use zoom slider only one time if project is new
      if (showSliderZoomTutorial) {
        // showZoomTutorial();
        setShowSliderZoomTutorial(false);
      }
    };

    // const handleLayout = (event: LayoutChangeEvent) => {
    //   'worklet';
    //   const { width, height, top, left } = event.nativeEvent.layout;

    //   // setting highlight zone for tutorial
    //   if (!isDesktop)
    //     setTutorialArea({
    //       key: 'sliderZoomArea',
    //       value: {
    //         width,
    //         height,
    //         x: left,
    //         y: top,
    //         borderRadius: 0,
    //       },
    //     });
    // };

    const userImage = preview.userImageArea.userImage;
    if (!userImage) {
      return (
        <UserImageAreaEmpty
          userImageArea={preview.userImageArea}
          disabled={disabled}
          focusOrder={0}
          showText={emptyStateWithText}
        />
      );
    }

    const isSelected: boolean = activeUserImageArea?.id === preview.userImageArea.id;
    const showFocusOverlay: boolean = preview.productId === activeProductId && !isSelected;
    const showGridOverlay: boolean = isSelected && editable && showGrid;
    const isQualityBadgeVisible: boolean =
      showBadges && userImage.qualityAlert && !!userImage.qualitySuggestions?.options.length;
    return (
      <>
        <UserImageArea style={style} userImageArea={preview.userImageArea} isSelected={isSelected}>
          <GestureArea
            disabled={disabled || preview.isAnyUserImageUpdating}
            onPan={event => dragImageChange(event, userImage)}
            onPanEnd={event => dragImageEnd(event, userImage)}
            onPinch={event => pinchHandler(event, userImage)}
            onPinchEnd={event => pinchEndHandler(event, userImage)}
            onTap={() => tapHandler(preview.userImageArea)}
            onHoverStart={() => setHoveringAreaId(preview.userImageArea.id)}
            onHoverEnd={() => setHoveringAreaId(prev => (prev === preview.userImageArea.id ? null : prev))}
          >
            {showGridOverlay && <UserImageAreaGridOverlay extBorderColor={newRoute ? '#FAA100' : neutrals.white} />}
            {showFocusOverlay && (
              <UserImageAreaFocusOverlay
                onPress={async () => tapHandler(preview.userImageArea)}
                userImageArea={preview.userImageArea}
              />
            )}
            {editable && (newRoute || editRoute) && (
              <UserImage
                editable={false}
                userImage={userImage}
                disableSwapImageAnimation={disableSwapImageAnimation}
                showOverflow
              />
            )}
            <UserImage
              editable={editable}
              userImage={userImage}
              disableSwapImageAnimation={disableSwapImageAnimation}
            />
          </GestureArea>

          <View style={{ position: 'absolute', zIndex: 200, bottom: 10, right: 10, flexDirection: 'row', gap: 8 }}>
            {isQualityBadgeVisible && <BadgeQualityAlert userImage={userImage} padding={qualityBadgePadding} />}
          </View>
        </UserImageArea>
      </>
    );
  },
);
